import { useMemo } from 'react';
import { DashboardPeriod, type ExternalDashboardFilters } from '../types/dashboard.types';

/**
 * Plage personnalisée dont il manque au moins une borne.
 *
 * Sert de garde `enabled` aux requêtes du dashboard : sans borne, l'API interprète
 * la période comme "Depuis le début" et rapatrie tout l'historique — pour rien,
 * puisque l'utilisateur est en train de choisir ses dates.
 */
export function isCustomRangeIncomplete(filters: ExternalDashboardFilters): boolean {
  return filters.period === DashboardPeriod.CUSTOM && (!filters.startDate || !filters.endDate);
}

export function usePeriodDates(filters: ExternalDashboardFilters) {
  return useMemo(() => {
    const now = new Date();
    let dateFrom: string | undefined;
    let dateTo: string | undefined;

    switch (filters.period) {
      case DashboardPeriod.DAY: {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        dateFrom = d.toISOString().split('T')[0];
        dateTo = now.toISOString().split('T')[0];
        break;
      }
      case DashboardPeriod.WEEK: {
        const day = now.getDay() || 7;
        const monday = new Date(now);
        monday.setDate(now.getDate() - day + 1);
        dateFrom = monday.toISOString().split('T')[0];
        dateTo = now.toISOString().split('T')[0];
        break;
      }
      case DashboardPeriod.MONTH: {
        dateFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        dateTo = now.toISOString().split('T')[0];
        break;
      }
      case DashboardPeriod.YEAR: {
        dateFrom = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
        dateTo = now.toISOString().split('T')[0];
        break;
      }
      case DashboardPeriod.LAST_12_MONTHS: {
        const d = new Date(now);
        d.setMonth(d.getMonth() - 12);
        dateFrom = d.toISOString().split('T')[0];
        dateTo = now.toISOString().split('T')[0];
        break;
      }
      case DashboardPeriod.CUSTOM: {
        dateFrom = filters.startDate;
        dateTo = filters.endDate;
        break;
      }
      case DashboardPeriod.ALL:
      default:
        break;
    }

    return { dateFrom, dateTo };
  }, [filters.period, filters.startDate, filters.endDate]);
}
